/* eslint-disable */
import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import WMSLayer from "@arcgis/core/layers/WMSLayer";
import _ from "lodash";
import { forkJoin, from, Observable, of, Subject } from "rxjs";
import { catchError, map, shareReplay, switchMap, takeUntil } from "rxjs/operators";
import { LayerType } from "~app/common/components/map/map.models";
import { FeatureService } from "~app/common/services/feature.service";
import { environment } from "~environments/environment";
import { IDynamicFeatureLayer } from "../../models/layer-list.models";
import { INode } from "../../models/layer-node.model";
import { ArcgisLayerLegend, ArcgisLayerLegendData, ArcgisLegendResponse } from "../models/arcgis-legend-response.model";
import { ILayerLegend, LayerLegend } from "../models/legend-models";

@Injectable({
    providedIn: "root"
})
export class LegendGraphicService {
    isServiceId = (id: string): boolean => !!this.getServiceConfig(id);

    private readonly legendDocumentService: LegendDocumentSingletonService;
    private readonly featureService = inject(FeatureService);
    private readonly ngUnsubscribe$ = new Subject<void>();

    constructor() {
        const http = inject(HttpClient);

        this.legendDocumentService = LegendDocumentSingletonService.getInstance(http);
    }

    /**
     * Return Observable<ILayerLegend[] för ett lager
     *
     * @param mapServiceId Namn featurelayer/vilken karttjänst. Definierade i environment
     * @param sublayerId Id för sublayer i en karttjänst
     */
    getLayerLegends(mapServiceId: string, sublayerId: number): Observable<ILayerLegend[]> {
        const serviceConfig = this.getServiceConfig(mapServiceId);
        if (!serviceConfig) {
            return of([]);
        }
        return this.legendsFromFeaturelayer(serviceConfig.url, sublayerId);
    }

    getWmsLayerLegends(wmsLayer: WMSLayer, sublayerName: string): Observable<ILayerLegend[]> {
        const wmsSublayer = wmsLayer.sublayers.find(x => x.name === sublayerName);
        if (!wmsSublayer?.legendEnabled) {
            return of([]);
        }
        return this.wmsLegendsFromFeaturelayer(wmsSublayer.legendUrl, sublayerName);
    }

    /**
     * Base load legends from map-config or INode array
     * @param nodes map config or node[]
     */
    baseLoadLegends(nodes: any[]): Observable<void> {
        const validLayerIds = this.getUniqueLayerIdsFromNodes(nodes);
        const calls$: Observable<ArcgisLegendResponse>[] = [];
        let allUniqueMapServices = _.uniqBy(validLayerIds.map(id => this.getServiceConfig(id))
            .filter(url => url != null && url != undefined && url.layerType !== LayerType.wms), (val) => val.url);

        this.featureService.getFeatureActive("VisaSpringfield")
            .pipe(takeUntil(this.ngUnsubscribe$)
            ).subscribe((visaSpringfieldEnabled: boolean) => {
                if (!visaSpringfieldEnabled) {
                    allUniqueMapServices = allUniqueMapServices.filter(map => map.id !== "Springfield");
                }
            });

        allUniqueMapServices.forEach(mapService => {
            calls$.push(this.legendDocumentService.ensureLegendDocumentFor(mapService.url, mapService.bypassProxy));
        });

        // map till undefined -> för att leverera Observable<void> tillbaks och inte Observable<void[]>
        return forkJoin(calls$).pipe(map(() => undefined));
    }

    /**
     * Return string[] with unique and valid mapServiceIds
     *
     * @param nodes Nodes to extract layerid:s from
     * @param layerIds string[] layerIds
     */
    private getUniqueLayerIdsFromNodes(nodes: INode[], layerIds: string[] = []) {
        nodes?.forEach(node => {
            if (node?.layerid && this.isServiceId(node.layerid)) {
                layerIds.push(node.layerid);
            }
            layerIds = [...new Set([...layerIds, ...this.getUniqueLayerIdsFromNodes(node.nodes)])];
        });
        return layerIds;
    }

    private getServiceConfig(mapServiceId: string): IDynamicFeatureLayer {
        const config = environment.dynamicFeatureLayers.filter(x => x.id === mapServiceId) as any;
        return config[0] as unknown as IDynamicFeatureLayer;
    }

    private legendsFromFeaturelayer(mapServiceUrl: string, sublayerId: number): Observable<ILayerLegend[]> {
        const doc = this.legendDocumentService.legendCache.get(mapServiceUrl);
        if (!doc) {
            return of([]);
        }
        return doc.pipe(map(legendResponse => {
            const featureLayer = legendResponse?.layers?.find(x => x.layerId === sublayerId);
            return featureLayer?.legend?.map(legend => LayerLegend.fromInterface(legend));
        }));

    }
     private wmsLegendsFromFeaturelayer(mapServiceUrl: string, sublayerName: string): Observable<ILayerLegend[]> {
       var doc = this.legendDocumentService.legendCache.get(mapServiceUrl);
        if (!doc) {
            // Hämta legend å lägg in i cahce
            this.legendDocumentService.ensureWmsLegendDocumentFor(mapServiceUrl, sublayerName, false).subscribe(x => {
                doc = this.legendDocumentService.legendCache.get(mapServiceUrl);
                if (!doc) {
                    return of([]);
                }
                return doc.pipe(map(legendResponse => {
                    const featureLayer = legendResponse?.layers?.find(x => x.layerName === sublayerName);
                    return featureLayer?.legend?.map(legend => LayerLegend.fromInterface(legend));
                }));
            });
        }
        doc = this.legendDocumentService.legendCache.get(mapServiceUrl);
        if (!doc) {
            return of([]);
        }
        return doc.pipe(map(legendResponse => {
            const featureLayer = legendResponse?.layers?.find(x => x.layerName === sublayerName);
            return featureLayer?.legend?.map(legend => LayerLegend.fromInterface(legend));
        }));
    }
}

class LegendDocumentSingletonService {
    readonly legendCache: Map<string, Observable<ArcgisLegendResponse>>;

    private static instance: LegendDocumentSingletonService;

    /**
     * Private constructor ensures single instance
     * @param http
     */
    private constructor(private readonly http: HttpClient) {
        this.legendCache = new Map<string, Observable<ArcgisLegendResponse>>();
    }

    static getInstance(http: HttpClient): LegendDocumentSingletonService {
        if (!LegendDocumentSingletonService.instance) {
            LegendDocumentSingletonService.instance = new LegendDocumentSingletonService(http);
        }

        return LegendDocumentSingletonService.instance;
    }

    ensureWmsLegendDocumentFor(wmsLegendUrl: string, sublayerName: string, bypassProxy: boolean): Observable<ArcgisLegendResponse> {

        if (this.legendCache.has(wmsLegendUrl)) {
            return this.legendCache.get(wmsLegendUrl);
        }

        const legendUrl = bypassProxy ? wmsLegendUrl : `${environment.mapProxy}?${wmsLegendUrl}`;

        const legend: ArcgisLayerLegend = { url: legendUrl, width: 20, height: 20 };
        const legendData: ArcgisLayerLegendData = { legend: [legend], layerName: sublayerName, legendGroups: [], layerType: LayerType.wms };
        const reader = (file: Blob) =>
            new Promise<string>((resolve, reject) => {
            const fileReader = new FileReader();
            fileReader.onload = () => resolve(fileReader.result as string);
            fileReader.onerror = reject;
            fileReader.readAsDataURL(file);
            });

        const obs$: Observable<ArcgisLegendResponse> = this.http.get(legendUrl, { responseType: 'blob' }).pipe(
            switchMap(blob => from(reader(blob))),
            map(imageData => {
            legend.imageData = imageData;
            return { layers: [legendData] };
            }),
            shareReplay(1),
            catchError(err => {
            console.error(err);
            return of(null);
            })
        );

        this.legendCache.set(wmsLegendUrl, obs$);
        return obs$;
    }

    ensureLegendDocumentFor(url: string, bypassProxy: boolean):Observable<ArcgisLegendResponse> {
    if (this.legendCache.has(url)) {
        return of(null);
    }
    const legendUrl = bypassProxy ? `${url}/legend?f=json` : `${environment.mapProxy}?${url}/legend?f=json`;

    const req = this.http.get<ArcgisLegendResponse>(legendUrl)
        .pipe(
            shareReplay(),
            catchError((err) => {
                console.error(err);
                return of(err)
            })
        );
    this.legendCache.set(url, req);
    return req;
}

}
